import React, { useMemo, useRef } from 'react';
import {
  Background,
  Connection,
  Controls,
  Edge,
  MiniMap,
  Node,
  NodeTypes,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import { Box, useTheme } from '@mui/material';
import { AgentDto } from '@shared/services/apiService/apiService';
import {
  buildHandoffCanvasGraph,
  HandoffCanvasEdge,
  HandoffCanvasNode,
  HandoffRuleEditorValue,
} from '@features/Admin/Agent/utils/handoff-rule-mapper';
import { AssistantNode } from './AssistantNode';
import 'reactflow/dist/style.css';

type HandoffCanvasProps = {
  rules: HandoffRuleEditorValue[];
  agents: AgentDto[];
  nodePositions?: Record<string, { x: number; y: number }>;
  selectedRuleId: string | null;
  onSelectRule: (ruleId: string) => void;
  onNodeRuleSelect: (agentId: string) => void;
  onConnectAgents: (sourceAgentId: string, targetAgentId: string) => void;
  onNodePositionChange?: (agentId: string, position: { x: number; y: number }) => void;
  onNodeSelect?: (agentId: string) => void;
  highlightedRuleId?: string | null;
  hasUnsavedChanges?: boolean;
  disabled?: boolean;
  height?: number | string;
};

const nodeTypes: NodeTypes = {
  assistantNode: AssistantNode,
};

const toReactFlowNodes = (nodes: HandoffCanvasNode[]): Node[] =>
  nodes.map((node) => ({
    ...node,
    type: 'assistantNode',
  }));

const toReactFlowEdges = (
  edges: HandoffCanvasEdge[],
  selectedRuleId: string | null,
  highlightedRuleId: string | null,
  hasUnsavedChanges: boolean,
  isDarkMode: boolean,
): Edge[] =>
  edges.map((edge) => ({
    ...edge,
    label: edge.data.label,
    labelShowBg: true,
    labelBgPadding: [8, 4],
    labelBgBorderRadius: 8,
    labelStyle: {
      fontSize: 12,
      fontWeight: 600,
      fill: isDarkMode ? '#d8e3f7' : '#344054',
    },
    labelBgStyle: {
      fill: isDarkMode ? '#0d1420' : '#FFFFFF',
      stroke: isDarkMode ? '#2a3c5f' : '#d0d5dd',
      strokeWidth: 1,
      opacity: 0.96,
    },
    animated: edge.data.ruleId === selectedRuleId,
    style: {
      strokeWidth:
        edge.data.ruleId === selectedRuleId
          ? 2.2
          : edge.data.ruleId === highlightedRuleId
            ? 2
            : 1.5,
      stroke:
        edge.data.ruleId === selectedRuleId
          ? isDarkMode
            ? '#63b3ff'
            : '#1976d2'
          : edge.data.ruleId === highlightedRuleId
            ? isDarkMode
              ? '#7cb6ff'
              : '#4f46e5'
            : isDarkMode
              ? '#64748b'
              : '#475467',
      strokeDasharray: hasUnsavedChanges ? '4 4' : undefined,
    },
    type: 'default',
  }));

export const HandoffCanvas: React.FC<HandoffCanvasProps> = ({
  rules,
  agents,
  nodePositions,
  selectedRuleId,
  onSelectRule,
  onNodeRuleSelect,
  onConnectAgents,
  onNodePositionChange,
  onNodeSelect,
  highlightedRuleId = null,
  hasUnsavedChanges = false,
  disabled = false,
  height = 520,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const reactFlowInstanceRef = useRef<{ fitView: (options?: { padding?: number }) => void } | null>(
    null,
  );
  const isDraggingRef = useRef(false);
  const graph = useMemo(
    () => buildHandoffCanvasGraph(rules, agents, nodePositions),
    [rules, agents, nodePositions],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(toReactFlowNodes(graph.nodes));
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    toReactFlowEdges(graph.edges, selectedRuleId, highlightedRuleId, hasUnsavedChanges, isDarkMode),
  );

  React.useEffect(() => {
    if (isDraggingRef.current) return;
    setNodes(toReactFlowNodes(graph.nodes));
  }, [graph.nodes, setNodes]);

  React.useEffect(() => {
    setEdges(
      toReactFlowEdges(
        graph.edges,
        selectedRuleId,
        highlightedRuleId,
        hasUnsavedChanges,
        isDarkMode,
      ),
    );
  }, [graph.edges, selectedRuleId, highlightedRuleId, hasUnsavedChanges, isDarkMode, setEdges]);

  React.useEffect(() => {
    if (!reactFlowInstanceRef.current || graph.nodes.length === 0) return;
    requestAnimationFrame(() => {
      reactFlowInstanceRef.current?.fitView({ padding: 0.15 });
    });
  }, [graph.nodes.length, graph.edges.length]);

  const onConnect = React.useCallback(
    (connection: Edge | Connection) => {
      if (disabled) return;
      if (connection.source && connection.target) {
        onConnectAgents(connection.source, connection.target);
      }
    },
    [disabled, onConnectAgents],
  );

  return (
    <Box
      sx={{
        width: '100%',
        height,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        backgroundColor: (theme) => theme.palette.background.default,
        overflow: 'hidden',
        '& .react-flow__attribution': {
          backgroundColor: (theme) => theme.palette.background.paper,
          color: 'text.secondary',
          borderTopLeftRadius: 8,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
        },
        '& .react-flow__controls': {
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 8,
          overflow: 'hidden',
          backgroundColor: (theme) => theme.palette.background.paper,
          margin: 16,
        },
        '& .react-flow__controls-button': {
          backgroundColor: (theme) => theme.palette.background.paper,
          color: 'text.primary',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          width: 32,
          height: 32,
          '&:hover': {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
        },
      }}>
      <ReactFlow
        onInit={(instance) => {
          reactFlowInstanceRef.current = instance;
          requestAnimationFrame(() => {
            instance.fitView({ padding: 0.15 });
          });
        }}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeClick={(_, edge) => {
          if (disabled) return;
          const ruleId = (edge.data as { ruleId?: string } | undefined)?.ruleId;
          if (ruleId) onSelectRule(ruleId);
        }}
        defaultEdgeOptions={{ markerEnd: { type: 'arrowclosed' } }}
        onNodeClick={(_, node) => {
          if (disabled || !node?.id) return;
          onNodeSelect?.(node.id);
          onNodeRuleSelect(node.id);
        }}
        onNodeDragStart={() => {
          isDraggingRef.current = true;
        }}
        onNodeDragStop={(_, node) => {
          if (disabled || !node?.id) {
            isDraggingRef.current = false;
            return;
          }
          onNodePositionChange?.(node.id, node.position);
          isDraggingRef.current = false;
        }}
        nodesDraggable={!disabled}
        nodesConnectable={!disabled}
        elementsSelectable={!disabled}
        fitView
        fitViewOptions={{ padding: 0.15, minZoom: 0.55, maxZoom: 1.15 }}
        minZoom={0.55}
        nodeTypes={nodeTypes}>
        <MiniMap
          pannable
          zoomable
          style={{
            backgroundColor: isDarkMode ? '#0b111d' : '#ffffff',
            border: `1px solid ${theme.palette.divider}`,
            width: 140,
            height: 90,
            borderRadius: 8,
            margin: 16,
          }}
          nodeColor={isDarkMode ? '#334a6a' : '#c6daf4'}
          maskColor={isDarkMode ? 'rgba(7, 11, 20, 0.72)' : 'rgba(237, 243, 251, 0.78)'}
        />
        <Controls />
        <Background
          color={isDarkMode ? 'rgba(148,163,184,0.2)' : 'rgba(0,0,0,0.06)'}
          gap={26}
          size={1}
        />
      </ReactFlow>
    </Box>
  );
};
